/** Commune — pure helpers for the town-square page.
 *
 *  The roadmap (content/roadmap.ts) is authored as three columns — now / next / later.
 *  Commune's "What's next" section reframes that same content for the community vote:
 *
 *    - `inProgress`  ← 'now'   — actively being built; shown, but NOT up for a vote
 *                                (you can't vote on what's already underway — that would be dishonest).
 *    - `strong`      ← 'next'  — "Strong candidates": the near-term shortlist, votable.
 *    - `considering` ← 'later' — "Under consideration": further out, votable.
 *
 *  Everything here is pure (no DOM, no I/O) so it unit-tests directly and the page stays thin.
 */
import { ROADMAP, type RoadmapItem } from '@/content/roadmap'

export interface CommuneRoadmap {
  /** 'now' items — in the works, not votable. */
  inProgress: RoadmapItem[]
  /** 'next' items — the votable shortlist. */
  strong: RoadmapItem[]
  /** 'later' items — votable, further out. */
  considering: RoadmapItem[]
}

function itemsForColumn(column: 'now' | 'next' | 'later'): RoadmapItem[] {
  return ROADMAP.find((c) => c.column === column)?.items ?? []
}

/** Reframe the three-column roadmap into Commune's in-progress / votable buckets. */
export function communeRoadmap(): CommuneRoadmap {
  return {
    inProgress: itemsForColumn('now'),
    strong: itemsForColumn('next'),
    considering: itemsForColumn('later'),
  }
}

/** A stable, slug-safe key for a roadmap item — the anchor a launch-time vote will attach to.
 *  Deterministic on the title so an optimistic local vote survives re-renders and matches the
 *  row the backend will persist against. Mirrors the domain slugifier's shape. */
export function voteKey(title: string): string {
  return (
    title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 64) || 'item'
  )
}
